import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  UserPlus,
  TrendingUp,
  Eye, 
  Search, 
  Filter, 
  X,
  Menu,
  ShoppingBag,
  DollarSign,
  Clock,
  Award,
  BarChart3,
  Calendar,
  Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Customer, Order, Payment } from '../../services/dataService';

interface CustomerStats {
  totalCustomers: number;
  activeCustomers: number;
  newThisMonth: number;
  averageOrderValue: number;
  topSpender: Customer | null;
  frequentCustomer: Customer | null;
}

interface CustomerAnalytics {
  monthlyGrowth: { month: string; customers: number; revenue: number }[];
  customerActivity: { date: string; newCustomers: number; orders: number }[];
  spendingDistribution: { range: string; count: number; percentage: number }[];
  topCustomers: { name: string; totalSpent: number; orderCount: number }[];
}

// Helper function to safely convert date
const safeToDate = (date: any): Date => {
  if (!date) return new Date();
  if (date instanceof Date) return date;
  if (date.toDate && typeof date.toDate === 'function') return date.toDate();
  if (typeof date === 'string' || typeof date === 'number') return new Date(date);
  return new Date();
};

// Helper function to safely format date
const safeFormatDate = (date: any): string => {
  try {
    const dateObj = safeToDate(date);
    return dateObj.toLocaleDateString();
  } catch (error) {
    return 'Unknown date';
  }
};

const Customers: React.FC = () => {
  const { tenantData } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'analytics'>('list');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats>({
    totalCustomers: 0,
    activeCustomers: 0,
    newThisMonth: 0,
    averageOrderValue: 0,
    topSpender: null,
    frequentCustomer: null
  });
  const [analytics, setAnalytics] = useState<CustomerAnalytics>({
    monthlyGrowth: [],
    customerActivity: [],
    spendingDistribution: [],
    topCustomers: []
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // New customer form state
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    notes: ''
  });

  // Listen for navbar actions
  useEffect(() => {
    const handleNavbarAction = (event: CustomEvent) => {
      const action = event.detail;
      if (action === 'add-customer') {
        setActiveTab('add');
      } else if (action === 'export-customers') {
        // TODO: Implement export functionality
        console.log('Export customers functionality to be implemented');
      }
    };

    window.addEventListener('navbar-action', handleNavbarAction as EventListener);
    
    return () => {
      window.removeEventListener('navbar-action', handleNavbarAction as EventListener);
    };
  }, []);

  // Calculate customer stats
  const calculateStats = useCallback((customers: Customer[], orders: Order[], payments: Payment[]) => {
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // Calculate basic stats
    const totalCustomers = customers.length;
    const activeCustomers = customers.filter(customer => {
      if (!customer.lastOrderDate) return false;
      const daysSinceLastOrder = (now.getTime() - safeToDate(customer.lastOrderDate).getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceLastOrder <= 30;
    }).length;
    
    const newThisMonth = customers.filter(customer => {
      const joinDate = safeToDate(customer.dateJoined);
      return joinDate >= thisMonth;
    }).length;
    
    const totalRevenue = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const averageOrderValue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;
    
    // Find top spender
    const topSpender = customers.length > 0 ? 
      customers.reduce((max, customer) => 
        (customer.totalSpent || 0) > (max.totalSpent || 0) ? customer : max
      ) : null;
    
    // Find most frequent customer
    const frequentCustomer = customers.length > 0 ? 
      customers.reduce((max, customer) => 
        (customer.totalOrders || 0) > (max.totalOrders || 0) ? customer : max
      ) : null;
    
    setStats({
      totalCustomers,
      activeCustomers,
      newThisMonth,
      averageOrderValue,
      topSpender,
      frequentCustomer
    });
  }, []);

  // Calculate customer analytics
  const calculateAnalytics = useCallback((customers: Customer[], orders: Order[], payments: Payment[]) => {
    // Monthly growth data (last 6 months)
    const monthlyGrowth = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStr = month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      const monthCustomers = customers.filter(c => {
        const customerDate = safeToDate(c.dateJoined);
        return customerDate.getMonth() === month.getMonth() && 
               customerDate.getFullYear() === month.getFullYear();
      }).length;
      
      const monthRevenue = payments.filter(p => {
        const paymentDate = safeToDate(p.createdAt);
        return paymentDate.getMonth() === month.getMonth() && 
               paymentDate.getFullYear() === month.getFullYear();
      }).reduce((sum, p) => sum + (p.amount || 0), 0);
      
      monthlyGrowth.push({ month: monthStr, customers: monthCustomers, revenue: monthRevenue });
    }

    // Customer activity (last 30 days)
    const customerActivity = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const newCustomers = customers.filter(c => {
        const customerDate = safeToDate(c.dateJoined);
        return customerDate.toDateString() === date.toDateString();
      }).length;
      
      const dayOrders = orders.filter(o => {
        const orderDate = safeToDate(o.createdAt);
        return orderDate.toDateString() === date.toDateString();
      }).length;
      
      customerActivity.push({ date: dateStr, newCustomers, orders: dayOrders });
    }

    // Spending distribution
    const spendingRanges = [
      { range: '₹0-500', min: 0, max: 500 },
      { range: '₹500-1000', min: 500, max: 1000 },
      { range: '₹1000-2000', min: 1000, max: 2000 },
      { range: '₹2000-5000', min: 2000, max: 5000 },
      { range: '₹5000+', min: 5000, max: Infinity }
    ];
    
    const spendingDistribution = spendingRanges.map(range => {
      const count = customers.filter(c => {
        const spent = c.totalSpent || 0;
        return spent >= range.min && spent < range.max;
      }).length;
      
      const percentage = customers.length > 0 ? (count / customers.length) * 100 : 0;
      return { range: range.range, count, percentage };
    });

    // Top customers
    const topCustomers = customers
      .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))
      .slice(0, 5)
      .map(c => ({
        name: c.name,
        totalSpent: c.totalSpent || 0,
        orderCount: c.totalOrders || 0
      }));

    setAnalytics({
      monthlyGrowth,
      customerActivity,
      spendingDistribution,
      topCustomers
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!tenantData?.id) return;

    try {
      setLoading(true);
      
      // Load customers, orders, and payments
      const [customersData, ordersData, paymentsData] = await Promise.all([
        dataService.getCustomers(tenantData.id),
        dataService.getOrders(tenantData.id),
        dataService.getPayments(tenantData.id)
      ]);

      setCustomers(customersData);
      
      // Calculate stats and analytics
      calculateStats(customersData, ordersData, paymentsData);
      calculateAnalytics(customersData, ordersData, paymentsData);

    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantData, calculateStats, calculateAnalytics]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter customers based on search and status
  useEffect(() => {
    let filtered = customers;

    if (searchTerm) {
      filtered = filtered.filter(customer =>
        (customer.name && customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.phone && customer.phone.includes(searchTerm))
      );
    }

    if (statusFilter !== 'all') {
      const now = new Date();
      filtered = filtered.map(customer => {
        const daysSinceLastOrder = customer.lastOrderDate ? 
          (now.getTime() - safeToDate(customer.lastOrderDate).getTime()) / (1000 * 60 * 60 * 24) : Infinity;
        return {
          ...customer,
          status: daysSinceLastOrder <= 30 ? 'active' : 'inactive' as 'active' | 'inactive'
        };
      }).filter(customer => customer.status === statusFilter);
    }

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, statusFilter]);

  const handleAddCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    if (!newCustomer.name.trim()) {
      setFormError('Please enter a customer name');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Check if customer already exists
      const existingCustomer = customers.find(c => 
        c.name.toLowerCase() === newCustomer.name.trim().toLowerCase()
      );
      
      if (existingCustomer) {
        setFormError('A customer with this name already exists');
        return;
      }

      // Create new customer in database
      const customerData = {
        name: newCustomer.name.trim(),
        phone: newCustomer.phone.trim() || undefined,
        notes: newCustomer.notes.trim() || undefined,
        status: 'active' as const,
        totalOrders: 0,
        totalSpent: 0,
        dateJoined: new Date()
      };

      await dataService.createCustomer(tenantData!.id, customerData);
      
      // Reload customers to get the new one
      await loadData();
      
      // Reset form and switch to list view
      setNewCustomer({ name: '', phone: '', notes: '' });
      setActiveTab('list');
      
    } catch (error) {
      console.error('Error adding customer:', error);
      setFormError('Failed to add customer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'list', label: 'Customer List', icon: Users, shortName: 'List' },
    { id: 'add', label: 'Add Customer', icon: UserPlus, shortName: 'Add' },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, shortName: 'Analytics' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-2 sm:p-4 md:p-6 lg:p-8">
      {/* Mobile Header with Tab Selector */}
      <div className="lg:hidden bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 sticky top-0 z-20 relative">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {(() => {
              const activeTabData = tabs.find(tab => tab.id === activeTab);
              if (!activeTabData) return null;
              const Icon = activeTabData.icon;
              return (
                <>
                  <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {activeTabData.label}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {activeTab === 'list' 
                        ? `${stats.totalCustomers} customers • ${stats.activeCustomers} active`
                        : activeTab === 'add' 
                        ? 'Add new customer to your database'
                        : 'Customer insights and analytics'
                      }
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            ) : (
              <Menu className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        {/* Mobile Tab Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 shadow-lg z-30">
            <div className="max-h-64 overflow-y-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id as any);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full px-4 py-4 flex items-center space-x-3 text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-r-4 border-primary-500'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{tab.label}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {tab.id === 'list' 
                          ? 'View and manage all customers'
                          : tab.id === 'add' 
                          ? 'Add new customer records'
                          : 'View customer statistics'
                        }
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Tab Navigation */}
      <div className="hidden lg:block border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md sticky top-0 z-10">
        <div className="px-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm xl:text-base flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tablet Tab Navigation */}
      <div className="hidden md:block lg:hidden border-b border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md sticky top-0 z-10">
        <div className="px-4">
          <nav className="grid grid-cols-3 gap-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 px-2 flex flex-col items-center justify-center space-y-1 text-xs font-medium transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="truncate w-full text-center">{tab.shortName}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto overflow-x-hidden">
          <div className="p-4 md:p-6 lg:p-8 w-full min-w-0 space-y-6">
            
            {/* Stats Cards - Only show on list tab */}
            {activeTab === 'list' && (
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 animate-fade-in-up">
                <div className="group bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:scale-105 transition-all duration-700 ease-out animate-fade-in-up">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <Users className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Total Customers</p>
                      <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCustomers}</p>
                    </div>
                  </div>
                </div>
                <div className="group bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:scale-105 transition-all duration-700 ease-out animate-fade-in-up animation-delay-100">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <TrendingUp className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Active</p>
                      <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{stats.activeCustomers}</p>
                    </div>
                  </div>
                </div>
                <div className="group bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:scale-105 transition-all duration-700 ease-out animate-fade-in-up animation-delay-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <UserPlus className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">New This Month</p>
                      <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{stats.newThisMonth}</p>
                    </div>
                  </div>
                </div>
                <div className="group bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg sm:rounded-2xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:scale-105 transition-all duration-700 ease-out animate-fade-in-up animation-delay-300">
                  <div className="flex items-center">
                    <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                      <DollarSign className="w-4 h-4 md:w-6 md:h-6 text-white" />
                    </div>
                    <div className="ml-4 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Avg Order</p>
                      <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">₹{stats.averageOrderValue.toFixed(0)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'list' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="flex items-center justify-between">
                  <div className="hidden md:flex md:flex-col md:tablet:flex-row md:tablet:items-center md:tablet:justify-between md:space-y-4 md:tablet:space-y-0 md:w-full">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        />
                      </div>
                      
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {filteredCustomers.length} of {customers.length} customers
                    </div>
                  </div>

                  {/* Mobile Filter Toggle */}
                  <div className="md:hidden w-full flex items-center justify-between">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {filteredCustomers.length} of {customers.length} customers
                    </div>
                    <button
                      onClick={() => setShowMobileFilters(!showMobileFilters)}
                      className={`p-2 rounded-lg transition-colors ${
                        showMobileFilters 
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {showMobileFilters ? (
                        <X className="w-5 h-5" />
                      ) : (
                        <Filter className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Mobile Filters */}
                <div className={`md:hidden ${showMobileFilters ? 'block' : 'hidden'} bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4`}>
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as any)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                        }}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Clear Filters
                      </button>
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>

                {/* Customer List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 animate-fade-in-up">
                  {filteredCustomers.map((customer, idx) => (
                    <div
                      key={customer.id}
                      className={`group bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:scale-105 transition-all duration-700 ease-out animate-fade-in-up`}
                      style={{ animationDelay: `${0.1 * idx}s` }}
                    >
                      <div className="flex items-center justify-between p-4">
                                <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                            <Users className="w-5 h-5 text-white" />
                                    </div>
                                <div>
                            <p className="text-base font-semibold text-gray-900 dark:text-white truncate">{customer.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{customer.phone || 'No phone'}</p>
                                  </div>
                                </div>
                                <button 
                                  onClick={() => navigate(`/customers/${customer.id}`)}
                          className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
                          title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                      <div className="px-4 pb-4">
                        <div className="flex items-center space-x-2 mb-1">
                          <ShoppingBag className="w-4 h-4 text-green-500 dark:text-green-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">{customer.totalOrders} orders</span>
                            </div>
                        <div className="flex items-center space-x-2 mb-1">
                          <DollarSign className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">₹{customer.totalSpent.toFixed(0)}</span>
                      </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-blue-400 dark:text-blue-300" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">Last: {customer.lastOrderDate ? safeFormatDate(customer.lastOrderDate) : 'Never'}</span>
                                          </div>
                                        </div>
                                          </div>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <div className="col-span-full text-center py-16">
                      <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                        <Users className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                                        </div>
                      <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No customers found</h3>
                      <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or filters.</p>
                                          </div>
                                        )}
                                      </div>
              </div>
            )}

            {activeTab === 'add' && (
              <div className="max-w-2xl">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Add New Customer</h3>
                  
                  {formError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                      <p className="text-red-700 dark:text-red-300 text-sm">{formError}</p>
                    </div>
                  )}
                  
                  <form onSubmit={handleAddCustomer} className="space-y-6">
                    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Customer Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={newCustomer.name}
                          onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="Enter customer name"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          placeholder="Enter phone number"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes
                      </label>
                      <textarea
                        value={newCustomer.notes}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                        placeholder="Additional notes about the customer"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('list');
                          setFormError('');
                          setNewCustomer({ name: '', phone: '', notes: '' });
                        }}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Adding Customer...' : 'Add Customer'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                {/* Top Customers */}
                <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
                  {/* Top Spender */}
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Spender</h3>
                      <Award className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
                    </div>
                    {stats.topSpender ? (
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                            <span className="text-lg font-medium text-yellow-600 dark:text-yellow-400">
                              {stats.topSpender.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{stats.topSpender.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{stats.topSpender.phone}</p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">
                            ₹{stats.topSpender.totalSpent.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">No data available</p>
                    )}
                  </div>

                  {/* Most Frequent Customer */}
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Most Frequent</h3>
                      <Clock className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                    </div>
                    {stats.frequentCustomer ? (
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <span className="text-lg font-medium text-blue-600 dark:text-blue-400">
                              {stats.frequentCustomer.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{stats.frequentCustomer.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{stats.frequentCustomer.phone}</p>
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                            {stats.frequentCustomer.totalOrders} orders
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">No data available</p>
                    )}
                  </div>
                </div>

                {/* Customer Activity Chart */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-200">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Activity</h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span>New Customers</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span>Orders</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="h-64 relative">
                    {/* Chart Container */}
                    <div className="absolute inset-0 flex items-end justify-between px-4 pb-8">
                      {analytics.customerActivity.slice(-7).map((data, index) => {
                        const maxNewCustomers = Math.max(...analytics.customerActivity.slice(-7).map(d => d.newCustomers));
                        const maxOrders = Math.max(...analytics.customerActivity.slice(-7).map(d => d.orders));
                        
                        const newCustomersHeight = maxNewCustomers > 0 ? (data.newCustomers / maxNewCustomers) * 100 : 0;
                        const ordersHeight = maxOrders > 0 ? (data.orders / maxOrders) * 100 : 0;
                        
                        return (
                          <div key={data.date} className="flex flex-col items-center space-y-2 flex-1">
                            {/* Bars */}
                            <div className="flex items-end space-x-1 w-full justify-center">
                              {/* New Customers Bar */}
                              <div 
                                className="w-3 bg-green-500 rounded-t-sm transition-all duration-500 hover:bg-green-600 relative group"
                                style={{ height: `${Math.max(newCustomersHeight, 8)}%` }}
                              >
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {data.newCustomers} new
                                </div>
                              </div>
                              
                              {/* Orders Bar */}
                              <div 
                                className="w-3 bg-blue-500 rounded-t-sm transition-all duration-500 hover:bg-blue-600 relative group"
                                style={{ height: `${Math.max(ordersHeight, 8)}%` }}
                              >
                                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                  {data.orders} orders
                                </div>
                              </div>
                            </div>
                            
                            {/* Date Label */}
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center transform -rotate-45 origin-center">
                              {data.date}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Y-axis labels */}
                    <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-xs text-gray-400 dark:text-gray-500 px-2">
                      {[100, 75, 50, 25, 0].map((value) => (
                        <div key={value} className="flex items-center">
                          <span>{value}%</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Grid lines */}
                    <div className="absolute inset-0 flex flex-col justify-between px-8 pb-8">
                      {[0, 1, 2, 3, 4].map((index) => (
                        <div key={index} className="border-t border-gray-200 dark:border-gray-600"></div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {analytics.customerActivity.slice(-7).reduce((sum, data) => sum + data.newCustomers, 0)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">New Customers (7 days)</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {analytics.customerActivity.slice(-7).reduce((sum, data) => sum + data.orders, 0)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Orders (7 days)</p>
                    </div>
                  </div>
                </div>

                {/* Analytics Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Monthly Growth Chart */}
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-300">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Monthly Growth</h3>
                    <div className="space-y-4">
                      {analytics.monthlyGrowth.map((data, index) => (
                        <div key={data.month} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
                              <span className="text-white text-sm font-medium">{index + 1}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{data.month}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{data.customers} customers</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                              ₹{data.revenue.toFixed(0)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Revenue</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Spending Distribution Chart */}
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-400">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Spending Distribution</h3>
                    <div className="space-y-4">
                      {analytics.spendingDistribution.map((data, index) => (
                        <div key={data.range} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{data.range}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{data.count} customers</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-blue-400 to-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${data.percentage}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{data.percentage.toFixed(1)}% of total</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Customer Activity Timeline */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-500">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Activity (Last 30 Days)</h3>
                  <div className="grid grid-cols-6 md:grid-cols-15 gap-2">
                    {analytics.customerActivity.slice(-15).map((data, index) => (
                      <div key={data.date} className="text-center">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 mb-1">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{data.date}</div>
                          <div className="space-y-1">
                            <div className="flex items-center justify-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs text-gray-900 dark:text-white ml-1">{data.newCustomers}</span>
                            </div>
                            <div className="flex items-center justify-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-xs text-gray-900 dark:text-white ml-1">{data.orders}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-center space-x-4 mt-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>New Customers</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Orders</span>
                    </div>
                  </div>
                </div>

                {/* Top Customers List */}
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-600">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Customers by Spending</h3>
                  <div className="space-y-4">
                    {analytics.topCustomers.map((customer, index) => (
                      <div key={customer.name} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium">{index + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{customer.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{customer.orderCount} orders</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">
                            ₹{customer.totalSpent.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Total Spent</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers; 